import {
    faCircleInfo, faMobileScreen, faClockRotateLeft, faCodeBranch, faLayerGroup,
    faBullseye, faPlug, faDatabase, faUserPlus,
} from '@fortawesome/free-solid-svg-icons';

import { gameSlug } from '../utils/gameSlug.js';

// Single source of truth for the game-creation onboarding flow: the 9 steps,
// their order, phase grouping, footer CTA and per-step hooks. The sidebar
// stepper, the wizard header and the footer all read from here so they cannot
// drift apart.

export const ONBOARDING_BASE = '/game/new';

export const ONBOARDING_PHASES = [
    { key: 'teach', label: 'Understand the game' },
    { key: 'model', label: 'Build the plan' },
    { key: 'wire', label: 'Connect it' },
    { key: 'team', label: 'Invite the team' },
];

export const ONBOARDING_STEPS = [
    // Only Game Information gates finishing setup (a game name), so it is not
    // skippable — `skipDisabled` suppresses its Skip button in the footer
    // (PRD: only this step requires input).
    { key: 'details', label: 'Game Information', phase: 'teach', icon: faCircleInfo, skipDisabled: true },
    { key: 'versions', label: 'Game Versions', phase: 'teach', icon: faMobileScreen },
    { key: 'history', label: 'Game History', phase: 'teach', icon: faClockRotateLeft },
    { key: 'configs', label: 'Game Configs', phase: 'teach', icon: faCodeBranch },
    { key: 'systems', label: 'Game Systems', phase: 'model', icon: faLayerGroup },
    { key: 'objectives', label: 'Objectives', phase: 'model', icon: faBullseye },
    { key: 'sdk', label: 'SDK Integration', phase: 'wire', icon: faPlug },
    { key: 'data', label: 'Data Connections', phase: 'wire', icon: faDatabase },
    { key: 'invite', label: 'Invite Your Team', phase: 'team', icon: faUserPlus },
];

export const TOTAL_ONBOARDING_STEPS = ONBOARDING_STEPS.length;

// The wizard URL carries the (already-created) game id: /game/new/{gameId}/{step}.
export const stepPath = (gameId, key) => `${ONBOARDING_BASE}/${gameId}/${key}`;
export const isOnboardingPath = (pathname) =>
    pathname === ONBOARDING_BASE || pathname.startsWith(`${ONBOARDING_BASE}/`);

// The game id embedded in an onboarding path (the first segment after the base),
// so the sidebar stepper can rebuild step links for the game currently in the URL.
export const onboardingGameId = (pathname) => {
    if (!pathname || !pathname.startsWith(`${ONBOARDING_BASE}/`)) return null;
    return pathname.slice(ONBOARDING_BASE.length + 1).split('/')[0] || null;
};

export const isOnboardingStep = (key) => !!key && ONBOARDING_STEPS.some((s) => s.key === key);
export const findStep = (key) => ONBOARDING_STEPS.find((s) => s.key === key) || null;
export const resolveStep = (key) => (isOnboardingStep(key) ? key : ONBOARDING_STEPS[0].key);
export const stepLabel = (key) => findStep(key)?.label ?? key;
export const stepIndex = (key) => ONBOARDING_STEPS.findIndex((s) => s.key === key);
export const stepNumber = (key) => {
    const i = stepIndex(key);
    return i === -1 ? null : i + 1;
};
export const nextStep = (key) => {
    const i = stepIndex(key);
    return i === -1 || i === ONBOARDING_STEPS.length - 1 ? null : ONBOARDING_STEPS[i + 1].key;
};
export const prevStep = (key) => {
    const i = stepIndex(key);
    return i <= 0 ? null : ONBOARDING_STEPS[i - 1].key;
};
export const stepPhase = (key) => findStep(key)?.phase ?? null;
export const phaseLabel = (key) => ONBOARDING_PHASES.find((p) => p.key === key)?.label ?? '';

// The wizard header's title: the game being set up, by the name in its Game Information form (the
// name the step bodies already use), else its catalogued name (a hard refresh, before the form has
// loaded), else "New game" until it has one. A blank name counts as none.
export const NEW_GAME_TITLE = 'New game';
export const wizardTitle = (formName, cataloguedName) => {
    const name = [formName, cataloguedName].find((n) => typeof n === 'string' && n.trim());
    return name ? name.trim() : NEW_GAME_TITLE;
};

// Where opening a game lands. A DRAFT game hasn't finished setup — the wizard's Finish activates it —
// so it opens the wizard with no step, and the wizard resumes at the furthest step reached from a
// fresh read of the stored profile (the cached catalogue's `profileSetup.setupStep` can be stale).
// Any other game opens its dashboard. Every "open this game" entry point (sidebar, header switcher,
// Games grid, setup banner) goes through here so they agree.
export const gameEntryPath = (game) => (game?.state === 'DRAFT'
    ? `${ONBOARDING_BASE}/${game.id}`
    : `/game/${gameSlug(game)}`);

// Footer primary action. Every step continues; the last one finishes — one rule
// so the button vocabulary stays identical across steps.
export const primaryCta = (key) => {
    const isFinish = stepIndex(key) === ONBOARDING_STEPS.length - 1;
    return {
        label: isFinish ? 'Finish setup' : 'Continue',
        isFinish,
        nextLabel: isFinish ? null : stepLabel(nextStep(key)),
    };
};
